import { formatPrice } from "@/lib/format";

const UNIT_LABELS: Record<string, string> = {
  piece: "Stück",
  bottle: "Flasche",
  pack: "Packung",
  kg: "kg",
};

export type ShopProduct = {
  id: string;
  name: string;
  description: string | null;
  shop_description: string | null;
  shop_image_url: string | null;
  unit: string;
  price_gross: string;
  requires_pickup_receipt: boolean;
};

interface ProductCardProps {
  article: ShopProduct;
  adding: boolean;
  onAdd: () => void;
}

function displayText(article: ShopProduct): string | null {
  return article.shop_description?.trim() || article.description?.trim() || null;
}

export function ProductCard({ article, adding, onAdd }: ProductCardProps) {
  const text = displayText(article);

  return (
    <article className="shop-card-interactive group flex h-full flex-col overflow-hidden !p-0 animate-slide-up">
      <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-brand-50 to-slate-100">
        {article.shop_image_url ? (
          <img
            src={article.shop_image_url}
            alt={article.name}
            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-brand-300">
            <svg className="h-12 w-12 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z"
              />
            </svg>
          </div>
        )}
        {article.requires_pickup_receipt ? (
          <span className="shop-badge-pickup absolute left-3 top-3 backdrop-blur-sm">Abholartikel</span>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-base font-semibold leading-snug text-slate-900 sm:text-lg">{article.name}</h2>
          <p className="shrink-0 text-lg font-bold tabular-nums text-brand-700">
            {formatPrice(article.price_gross)}
          </p>
        </div>

        {text ? (
          <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-slate-600">{text}</p>
        ) : (
          <div className="flex-1" />
        )}

        <p className="mt-3 text-xs font-medium uppercase tracking-wide text-slate-400">
          {UNIT_LABELS[article.unit] ?? article.unit}
        </p>

        <button
          type="button"
          disabled={adding}
          onClick={onAdd}
          className="shop-btn-primary mt-4 w-full"
        >
          {adding ? (
            <>
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              Wird hinzugefügt…
            </>
          ) : (
            "In den Warenkorb"
          )}
        </button>
      </div>
    </article>
  );
}

export function ProductCardSkeleton() {
  return (
    <div className="shop-card overflow-hidden !p-0">
      <div className="shop-skeleton aspect-[4/3]" />
      <div className="space-y-3 p-5">
        <div className="shop-skeleton h-5 w-3/4" />
        <div className="shop-skeleton h-4 w-full" />
        <div className="shop-skeleton h-4 w-2/3" />
        <div className="shop-skeleton mt-4 h-11 w-full rounded-xl" />
      </div>
    </div>
  );
}
